// Client twin of utils/shiftSchedule.js on the server — keep the expansion rules in sync.
import moment from "moment";

export const RECUR_FORMAT = "DD/MMM/YYYY HH:mm";
export const DATE_KEY = "YYYYMMDD";
export const DEFAULT_SHIFT_COLOR = "#3174ad";

// Default German working-time rules (ArbZG): 10h max per day, 11h rest, 48h max per week
export const DEFAULT_WORK_RULES = {
  weeklyHours: 40,
  maxWeeklyHours: 48,
  maxDailyHours: 10,
  minRestHours: 11
};

// Sent with every save so the server reads wall-clock times in the planner's timezone
export const getTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
};

/**
 * startRecur/endRecur store the wall-clock time the user picked ("05/Apr/2026 09:00").
 * They are the source of truth for a shift's daily time: start/end used to be sent as the
 * same string and parsed in the *server's* timezone, so old records can be offset by hours.
 */
export const parseRecurDate = str => {
  if (!str) return null;
  const m = moment(str, [RECUR_FORMAT, "DD/MMM/YYYY"]);
  return m.isValid() ? m : null;
};

export const isRecurringShift = shift =>
  Array.isArray(shift?.daysOfWeek) && shift.daysOfWeek.length > 0;

const timeOf = m => ({ hours: m.hours(), minutes: m.minutes() });

// Daily start/end time. Falls back to start/end for legacy records whose recur strings carry no time.
const getDailyTimes = (shift, startRecur, endRecur) => {
  const recurHasTime =
    startRecur.hours() + startRecur.minutes() + endRecur.hours() + endRecur.minutes() > 0;
  if (recurHasTime || !shift.start || !shift.end) {
    return { from: timeOf(startRecur), to: timeOf(endRecur) };
  }
  return { from: timeOf(moment(shift.start)), to: timeOf(moment(shift.end)) };
};

/**
 * Expand a stored shift into the calendar occurrences that intersect [rangeStart, rangeEnd],
 * applying its per-day exceptions. Each occurrence keeps the stored shift in `_shift`, its
 * original date in `_dateKey`, and is flagged `cancelled` / `_moved` / `_customStaff`.
 */
export const expandShift = (shift, rangeStart, rangeEnd) => {
  const base = {
    ...shift,
    _shift: shift,
    _originalId: shift._id,
    colorCode: shift.color || DEFAULT_SHIFT_COLOR
  };
  const exceptions = {};
  (shift.exceptions || []).forEach(exc => (exceptions[exc.date] = exc));

  const makeOccurrence = (dateKey, start, end, id) => {
    const exc = exceptions[dateKey];
    const moved = !!(exc?.start && exc?.end);
    const occStart = moved ? moment(exc.start) : start;
    const occEnd = moved ? moment(exc.end) : end;
    const outside = (s, e) => e.isBefore(rangeStart) || s.isAfter(rangeEnd);
    if (outside(start, end) && outside(occStart, occEnd)) return null;
    const customStaff = Array.isArray(exc?.employees);
    return {
      ...base,
      _id: id,
      _dateKey: dateKey,
      _moved: moved,
      _customStaff: customStaff,
      cancelled: !!exc?.cancelled,
      employees: customStaff ? exc.employees : shift.employees || [],
      start: occStart.toDate(),
      end: occEnd.toDate()
    };
  };

  const startRecur = parseRecurDate(shift.startRecur);
  const endRecur = parseRecurDate(shift.endRecur);
  const days = isRecurringShift(shift) ? shift.daysOfWeek.map(Number) : [];

  // One-off shift: a single block from start to end
  if (days.length === 0 || !startRecur || !endRecur) {
    const start = startRecur || moment(shift.start);
    const end = endRecur || moment(shift.end);
    const occ = makeOccurrence(start.format(DATE_KEY), start, end, shift._id);
    return occ ? [occ] : [];
  }

  const { from, to } = getDailyTimes(shift, startRecur, endRecur);
  // Begin a day early so an overnight occurrence from the previous day is still shown
  const cursor = moment.max(
    startRecur.clone().startOf("day"),
    moment(rangeStart).subtract(1, "day").startOf("day")
  );
  const limit = moment.min(endRecur.clone().endOf("day"), moment(rangeEnd).endOf("day"));

  const events = [];
  while (cursor.isSameOrBefore(limit)) {
    if (days.includes(cursor.day())) {
      const start = cursor.clone().hours(from.hours).minutes(from.minutes);
      const end = cursor.clone().hours(to.hours).minutes(to.minutes);
      if (!end.isAfter(start)) end.add(1, "day"); // overnight shift
      const key = cursor.format(DATE_KEY);
      const occ = makeOccurrence(key, start, end, `${shift._id}_${key}`);
      if (occ) events.push(occ);
    }
    cursor.add(1, "day");
  }
  return events;
};

// Period a view covers (month views span whole weeks); `padded` adds a week either side
export const getViewPeriod = (date, view) => {
  const unit = view === "day" ? "day" : view === "week" ? "week" : "month";
  const start = moment(date).startOf(unit);
  const end = moment(date).endOf(unit);
  if (unit === "month") {
    start.startOf("week");
    end.endOf("week");
  }
  return { start: start.toDate(), end: end.toDate() };
};

export const getVisibleRange = (date, view) => {
  const { start, end } = getViewPeriod(date, view);
  return {
    start: moment(start).subtract(7, "days").toDate(),
    end: moment(end).add(7, "days").toDate()
  };
};

const hoursBetween = (a, b) => (b - a) / 36e5;
export const weekKey = date => moment(date).format("GGGG-[W]WW");

/**
 * Planned hours and working-time violations per employee and ISO week.
 * Returns { rows, issues } where issues[occurrenceId][employeeId] = [message].
 */
export const computeWorkload = (events, rules = DEFAULT_WORK_RULES) => {
  const byEmployee = {};
  events
    .filter(e => !e.cancelled)
    .forEach(event =>
      (event.employees || []).forEach(emp => {
        if (!byEmployee[emp._id]) byEmployee[emp._id] = { employee: emp, events: [] };
        byEmployee[emp._id].events.push(event);
      })
    );

  const rows = [];
  const issues = {};
  const addIssue = (event, employeeId, message) => {
    issues[event._id] = issues[event._id] || {};
    (issues[event._id][employeeId] = issues[event._id][employeeId] || []).push(message);
  };

  Object.values(byEmployee).forEach(({ employee, events: list }) => {
    list.sort((a, b) => a.start - b.start);
    const weeks = {};
    const days = {};
    list.forEach((event, i) => {
      const hours = hoursBetween(event.start, event.end);
      const wk = weekKey(event.start);
      weeks[wk] = weeks[wk] || { planned: 0, daily: [], rest: [] };
      weeks[wk].planned += hours;

      const day = moment(event.start).format("YYYY-MM-DD");
      days[day] = (days[day] || 0) + hours;

      const prev = list[i - 1];
      if (prev && event.start >= prev.end) {
        const rest = hoursBetween(prev.end, event.start);
        if (rest < rules.minRestHours) {
          const msg = `Only ${rest.toFixed(1)}h rest after "${prev.title}" (min ${rules.minRestHours}h)`;
          weeks[wk].rest.push(`${moment(event.start).format("ddd DD/MMM")}: ${msg}`);
          addIssue(event, employee._id, msg);
        }
      }
    });

    Object.entries(days).forEach(([day, hours]) => {
      if (hours > rules.maxDailyHours) {
        const wk = weekKey(day);
        const msg = `${hours.toFixed(1)}h planned on ${moment(day).format("ddd DD/MMM")} (max ${rules.maxDailyHours}h)`;
        weeks[wk].daily.push(msg);
        list
          .filter(e => moment(e.start).format("YYYY-MM-DD") === day)
          .forEach(e => addIssue(e, employee._id, msg));
      }
    });

    Object.entries(weeks).forEach(([week, w]) =>
      rows.push({
        key: `${employee._id}_${week}`,
        employee,
        week,
        planned: w.planned,
        overtime: Math.max(0, w.planned - rules.weeklyHours),
        overMax: w.planned > rules.maxWeeklyHours,
        dailyViolations: w.daily,
        restViolations: w.rest
      })
    );
  });

  rows.sort(
    (a, b) =>
      a.week.localeCompare(b.week) ||
      `${a.employee.firstName} ${a.employee.lastName}`.localeCompare(
        `${b.employee.firstName} ${b.employee.lastName}`
      )
  );
  return { rows, issues };
};
